/* eslint-disable import/no-duplicates */
import languages from '@cospired/i18n-iso-languages'
import { AppFlavor } from 'shared/src/model/common'
import { BaseLocalStorage } from 'shared/src/service/localStorage/BaseLocalStorage'
import { enumValueExists, getEnumKeyByValue } from 'shared/src/util/typeChecks'
import { isValue } from 'shared/src/util/typeGuard'
import { CommonUrlQueryKeys } from 'shared/src/util/url'

export enum LanguageCode {
  ALBANIAN = 'sq',
  ARABIC = 'ar',
  BOSNIAN = 'bs',
  BULGARIAN = 'bg',
  CHINESE = 'zh',
  CROATIAN = 'hr',
  CZECH = 'cs',
  DANISH = 'da',
  DUTCH = 'nl',
  ENGLISH = 'en',
  ESTONIAN = 'et',
  FINNISH = 'fi',
  FRENCH = 'fr',
  GERMAN = 'de',
  GREEK = 'el',
  HUNGARIAN = 'hu',
  IRISH = 'ga',
  ITALIAN = 'it',
  JAPANESE = 'ja',
  LATVIAN = 'lv',
  LITHUANIAN = 'lt',
  NORWEGIAN = 'no',
  PERSIAN = 'fa',
  POLISH = 'pl',
  PORTUGUESE = 'pt',
  ROMANIAN = 'ro',
  RUSSIAN = 'ru',
  SERBIAN = 'sr',
  SLOVAK = 'sk',
  SLOVENIAN = 'sl',
  SPANISH = 'es',
  SWEDISH = 'sv',
  TURKISH = 'tr',
  UKRAINIAN = 'uk',
}

export enum Locale {
  ALBANIAN = 'sq-AL',
  ARABIC = 'ar-SA',
  BOSNIAN = 'bs-BA',
  BULGARIAN = 'bg-BG',
  CHINESE = 'zh-CN',
  CROATIAN = 'hr-HR',
  CZECH = 'cs-CZ',
  DANISH = 'da-DK',
  DUTCH = 'nl-NL',
  ENGLISH = 'en-GB',
  ESTONIAN = 'et-EE',
  FINNISH = 'fi-FI',
  FRENCH = 'fr-FR',
  GERMAN = 'de-DE',
  GREEK = 'el-GR',
  HUNGARIAN = 'hu-HU',
  IRISH = 'ga-IE',
  ITALIAN = 'it-IT',
  JAPANESE = 'ja-JP',
  LATVIAN = 'lv-LV',
  LITHUANIAN = 'lt-LT',
  NORWEGIAN = 'no-NO',
  PERSIAN = 'fa-IR',
  POLISH = 'pl-PL',
  PORTUGUESE = 'pt-PT',
  ROMANIAN = 'ro-RO',
  RUSSIAN = 'ru-RU',
  SERBIAN = 'sr-RS',
  SLOVAK = 'sk-SK',
  SLOVENIAN = 'sl-SI',
  SPANISH = 'es-ES',
  SWEDISH = 'sv-SE',
  TURKISH = 'tr-TR',
  UKRAINIAN = 'uk-UA',
}

export const langCodeToLocaleMap: Record<LanguageCode, Locale> = {
  [LanguageCode.ALBANIAN]: Locale.ALBANIAN,
  [LanguageCode.ARABIC]: Locale.ARABIC,
  [LanguageCode.BOSNIAN]: Locale.BOSNIAN,
  [LanguageCode.BULGARIAN]: Locale.BULGARIAN,
  [LanguageCode.CHINESE]: Locale.CHINESE,
  [LanguageCode.CROATIAN]: Locale.CROATIAN,
  [LanguageCode.CZECH]: Locale.CZECH,
  [LanguageCode.DANISH]: Locale.DANISH,
  [LanguageCode.DUTCH]: Locale.DUTCH,
  [LanguageCode.ENGLISH]: Locale.ENGLISH,
  [LanguageCode.ESTONIAN]: Locale.ESTONIAN,
  [LanguageCode.FINNISH]: Locale.FINNISH,
  [LanguageCode.FRENCH]: Locale.FRENCH,
  [LanguageCode.GERMAN]: Locale.GERMAN,
  [LanguageCode.GREEK]: Locale.GREEK,
  [LanguageCode.HUNGARIAN]: Locale.HUNGARIAN,
  [LanguageCode.IRISH]: Locale.IRISH,
  [LanguageCode.ITALIAN]: Locale.ITALIAN,
  [LanguageCode.JAPANESE]: Locale.JAPANESE,
  [LanguageCode.LATVIAN]: Locale.LATVIAN,
  [LanguageCode.LITHUANIAN]: Locale.LITHUANIAN,
  [LanguageCode.NORWEGIAN]: Locale.NORWEGIAN,
  [LanguageCode.PERSIAN]: Locale.PERSIAN,
  [LanguageCode.POLISH]: Locale.POLISH,
  [LanguageCode.PORTUGUESE]: Locale.PORTUGUESE,
  [LanguageCode.ROMANIAN]: Locale.ROMANIAN,
  [LanguageCode.RUSSIAN]: Locale.RUSSIAN,
  [LanguageCode.SERBIAN]: Locale.SERBIAN,
  [LanguageCode.SLOVAK]: Locale.SLOVAK,
  [LanguageCode.SLOVENIAN]: Locale.SLOVENIAN,
  [LanguageCode.SPANISH]: Locale.SPANISH,
  [LanguageCode.SWEDISH]: Locale.SWEDISH,
  [LanguageCode.TURKISH]: Locale.TURKISH,
  [LanguageCode.UKRAINIAN]: Locale.UKRAINIAN,
}

export const localeToLangCodeMap: Record<Locale, LanguageCode> = {
  [Locale.ALBANIAN]: LanguageCode.ALBANIAN,
  [Locale.ARABIC]: LanguageCode.ARABIC,
  [Locale.BOSNIAN]: LanguageCode.BOSNIAN,
  [Locale.BULGARIAN]: LanguageCode.BULGARIAN,
  [Locale.CHINESE]: LanguageCode.CHINESE,
  [Locale.CROATIAN]: LanguageCode.CROATIAN,
  [Locale.CZECH]: LanguageCode.CZECH,
  [Locale.DANISH]: LanguageCode.DANISH,
  [Locale.DUTCH]: LanguageCode.DUTCH,
  [Locale.ENGLISH]: LanguageCode.ENGLISH,
  [Locale.ESTONIAN]: LanguageCode.ESTONIAN,
  [Locale.FINNISH]: LanguageCode.FINNISH,
  [Locale.FRENCH]: LanguageCode.FRENCH,
  [Locale.GERMAN]: LanguageCode.GERMAN,
  [Locale.GREEK]: LanguageCode.GREEK,
  [Locale.HUNGARIAN]: LanguageCode.HUNGARIAN,
  [Locale.IRISH]: LanguageCode.IRISH,
  [Locale.ITALIAN]: LanguageCode.ITALIAN,
  [Locale.JAPANESE]: LanguageCode.JAPANESE,
  [Locale.LATVIAN]: LanguageCode.LATVIAN,
  [Locale.LITHUANIAN]: LanguageCode.LITHUANIAN,
  [Locale.NORWEGIAN]: LanguageCode.NORWEGIAN,
  [Locale.PERSIAN]: LanguageCode.PERSIAN,
  [Locale.POLISH]: LanguageCode.POLISH,
  [Locale.PORTUGUESE]: LanguageCode.PORTUGUESE,
  [Locale.ROMANIAN]: LanguageCode.ROMANIAN,
  [Locale.RUSSIAN]: LanguageCode.RUSSIAN,
  [Locale.SERBIAN]: LanguageCode.SERBIAN,
  [Locale.SLOVAK]: LanguageCode.SLOVAK,
  [Locale.SLOVENIAN]: LanguageCode.SLOVENIAN,
  [Locale.SPANISH]: LanguageCode.SPANISH,
  [Locale.SWEDISH]: LanguageCode.SWEDISH,
  [Locale.TURKISH]: LanguageCode.TURKISH,
  [Locale.UKRAINIAN]: LanguageCode.UKRAINIAN,
}

type LocaleSupport = {
  locale: Locale
  supportFor: string[]
}

const localeSupport: LocaleSupport[] = [
  { locale: Locale.FRENCH, supportFor: [LanguageCode.FRENCH] },
  { locale: Locale.GERMAN, supportFor: [LanguageCode.GERMAN] },
]

export const getLanguageConfig = (): { locale: Locale; langCode: LanguageCode } => {
  let locale = getLocale()
  let langCode = locale.split('-')[0] as LanguageCode

  if (!isValue(locale) || !enumValueExists(langCode, LanguageCode)) {
    locale = Locale.ENGLISH
    langCode = LanguageCode.ENGLISH
  }

  document.documentElement.lang = langCode

  return {
    locale,
    langCode,
  }
}

const getLocale = (): Locale => {
  const langFromUrl = new URLSearchParams(window.location.search).get(CommonUrlQueryKeys.LANG)
  if (isValue(langFromUrl) && enumValueExists(langFromUrl, LanguageCode)) {
    return langCodeToLocaleMap[langFromUrl as LanguageCode]
  }

  const localeFromLocalStorage = BaseLocalStorage.readLocale()
  if (isValue(localeFromLocalStorage) && enumValueExists(localeFromLocalStorage, Locale)) {
    return localeFromLocalStorage as Locale
  }

  return localeSupport.find((it) => it.supportFor.includes(navigator.language.split('-')[0]))?.locale ?? Locale.ENGLISH
}

export const getDeviceBrandName = (): string => {
  const { location } = window
  const appFlavor = getEnumKeyByValue<AppFlavor>(
    AppFlavor,
    new URLSearchParams(location.search).get(CommonUrlQueryKeys.APP_FLAVOR) ?? undefined,
  )
  if (appFlavor === AppFlavor.PELLETTRACKER) {
    return 'pellet tracker'
  }
  return 'Fox'
}

export const getHardwareName = (): string => {
  const { location } = window
  const appFlavor = getEnumKeyByValue<AppFlavor>(
    AppFlavor,
    new URLSearchParams(location.search).get(CommonUrlQueryKeys.APP_FLAVOR) ?? undefined,
  )
  if (appFlavor === AppFlavor.PELLETTRACKER) {
    return 'pellet tracker'
  }
  return 'FoxHardware'
}

export class Language {
  constructor(public code: LanguageCode) {}

  private getI18nLanguageNameByLanguageCode(code: LanguageCode): string | undefined {
    const { langCode } = getLanguageConfig()
    return languages.getName(code, langCode)
  }

  get i18nName(): string | undefined {
    return this.getI18nLanguageNameByLanguageCode(this.code)
  }
}
